import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { CoraChatView } from "@/components/help/CoraChatView";
import { HelpArticleView } from "@/components/help/HelpArticleView";
import { HelpPanelRail } from "@/components/help/HelpPanelRail";
import { HelpScreenView } from "@/components/help/HelpScreenView";
import { HelpSearchBox } from "@/components/help/HelpSearchBox";
import { HelpSearchResultsView } from "@/components/help/HelpSearchResultsView";
import { HelpSectionView } from "@/components/help/HelpSectionView";
import { HelpWatchView } from "@/components/help/HelpWatchView";
import { isCoraEnabled } from "@/config/publicEnv";
import { CORA_UI } from "@/constants/cora";
import {
  HELP_RAIL_FALLBACK_LABEL,
  HELP_SEARCH,
  HELP_TOPIC_LABELS,
  HELP_WATCH,
} from "@/constants/help";
import { useHelpPanel } from "@/context/HelpPanelContext";
import { useCoraSettings } from "@/hooks/useCoraSettings";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import type { CoraChatEntry, CoraConversation } from "@/interfaces/cora";
import type { HelpPanelView, HelpSection } from "@/interfaces/help";
import { helpScreenRequestKeys, resolveHelpScreenContext } from "@/utils/help-context-helper";
import { helpPanelTabWrapTarget } from "@/utils/help-focus-helper";

const SCREEN_VIEW: HelpPanelView = { kind: "screen" };
const SEARCH_VIEW: HelpPanelView = { kind: "search" };
const WATCH_VIEW: HelpPanelView = { kind: "watch" };
const CHAT_VIEW: HelpPanelView = { kind: "chat" };

/** Where a plain `open()` lands: the chat when Cora is on, else the screen's article cut. */
function homeView(): HelpPanelView {
  return isCoraEnabled() ? CHAT_VIEW : SCREEN_VIEW;
}

/**
 * True when "talk to a human" has nothing to add: a person is already asked for, Cora's latest
 * line is already the invitation, or the conversation was already handed to the team.
 */
function isHumanPromptRedundant(conversation: CoraConversation): boolean {
  if (conversation.handoffRequested) return true;
  if (conversation.entries.some((e) => e.turn?.action === "pass_to_team")) return true;
  const latestCora = conversation.entries.findLast((e) => e.role === "cora");
  return latestCora?.kind === "human-prompt";
}

/**
 * The Help Center slide-over: a 66.67%-wide panel (capped at 960px) that mounts once in the
 * authenticated layout and is opened by the sidebar's Support item. Sized and eased to match the
 * design of record's `.hc-panel` (content/design/client-experience-prototype.html).
 *
 * Always mounted so its slide transition can play both opening and closing; `inert` (imperative —
 * this repo's React types don't have the JSX `inert` prop yet, see RedemptionPhoneField's bottom
 * sheet) keeps it out of the tab order and off the accessibility tree while closed, rather than
 * unmounting it.
 *
 * The content pane (right) shows the current view: the screen's article cut, the search results
 * while the header box holds a query, a topic's article listing, the walkthrough-video listing, the
 * Cora chat (shown when isCoraEnabled() is true), or one article by slug, opened in place — an
 * article opened from a topic or Watch goes Back there, and one opened from the chat goes Back to
 * Cora. A plain `open()` call lands on the chat (or the screen view when Cora is off); `open(slug)`
 * (the `/support?article=<slug>` deep link) lands straight on that article. The conversation
 * outlives navigation and closing the panel; only "Start a new conversation" or a reload clears it.
 */
export function HelpPanel() {
  const { isOpen, close, openSlug } = useHelpPanel();
  const panelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const coraSettings = useCoraSettings();
  const [view, setView] = useState<HelpPanelView>(homeView);
  // True when the current view was reached from inside the panel (a row, an internal link, Back):
  // the control that triggered it has unmounted, so the new view takes focus. Opening the panel
  // leaves it false — the search box takes focus then.
  const [viewTakesFocus, setViewTakesFocus] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query.trim(), HELP_SEARCH.DEBOUNCE_MS);
  const hasSearchQuery = query.trim().length >= HELP_SEARCH.MIN_QUERY_LENGTH;
  // Lives here, not in CoraChatView, so it survives the chat view unmounting.
  const [conversation, setConversation] = useState<CoraConversation | null>(null);
  const [focusNewConversationTick, setFocusNewConversationTick] = useState(0);
  const isChatShown = view.kind === "chat" || (view.kind === "article" && !!view.fromChat);

  function showView(next: HelpPanelView, takesFocus: boolean) {
    setView(next);
    setViewTakesFocus(takesFocus);
  }

  // Adds Cora's "what's your question?" line and routes the user's next message to the team.
  function requestHumanPrompt() {
    const prompt: CoraChatEntry = {
      id: crypto.randomUUID(),
      role: "cora",
      kind: "human-prompt",
      text: coraSettings.humanPrompt,
    };
    const newId = crypto.randomUUID();
    setConversation((prev) => {
      if (prev === null) return { id: newId, entries: [prompt], handoffRequested: true };
      if (isHumanPromptRedundant(prev)) return prev;
      return { ...prev, entries: [...prev.entries, prompt], handoffRequested: true };
    });
  }

  // Never takes focus: this runs on every keystroke and focus has to stay in the box.
  function changeQuery(next: string) {
    setQuery(next);
    const isSearch = next.trim().length >= HELP_SEARCH.MIN_QUERY_LENGTH;
    showView(isSearch ? SEARCH_VIEW : SCREEN_VIEW, false);
  }

  const screenContext = useMemo(
    () => resolveHelpScreenContext(location.pathname),
    [location.pathname],
  );

  // A layout effect so `inert` lifts before useFocusTrap's passive effect focuses the panel — an
  // inert node can't take focus, so a passive effect here would race the trap on declaration order.
  useLayoutEffect(() => {
    if (panelRef.current) panelRef.current.inert = !isOpen;
  }, [isOpen]);

  useFocusTrap(isOpen, panelRef);

  useEffect(() => {
    if (!isOpen) return;
    setView(openSlug ? { kind: "article", slug: openSlug } : homeView());
    setViewTakesFocus(false);
    setQuery("");
  }, [isOpen, openSlug]);

  // Declared after useFocusTrap so it runs after the trap's own `node.focus()` on open.
  useEffect(() => {
    if (isOpen) searchInputRef.current?.focus();
  }, [isOpen]);

  function resetSearch() {
    setQuery("");
    showView(SCREEN_VIEW, false);
  }

  function selectSection(section: HelpSection, takesFocus: boolean) {
    setQuery("");
    showView({ kind: "section", section }, takesFocus);
  }

  function selectChat() {
    setQuery("");
    showView(CHAT_VIEW, true);
  }

  // Once handed off there is no composer to ask in, so point at "Start a new conversation".
  function talkToHuman() {
    const handedOff = conversation?.entries.some((e) => e.turn?.action === "pass_to_team");
    if (!handedOff) {
      selectChat();
      requestHumanPrompt();
      return;
    }
    if (view.kind !== "chat") selectChat();
    setFocusNewConversationTick((tick) => tick + 1);
  }

  // The narrow-screen way out of the chat, where the rail is hidden.
  function backToHelp() {
    setQuery("");
    showView(SCREEN_VIEW, true);
  }

  function selectWatch() {
    setQuery("");
    showView(WATCH_VIEW, false);
  }

  function clearSearch() {
    resetSearch();
    searchInputRef.current?.focus();
  }

  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close();
      if (event.key !== "Tab" || event.shiftKey || event.defaultPrevented || !panelRef.current) {
        return;
      }
      const wrapTo = helpPanelTabWrapTarget(panelRef.current, document.activeElement);
      if (wrapTo) {
        event.preventDefault();
        wrapTo.focus();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, close]);

  return (
    <>
      <div
        onClick={close}
        aria-hidden="true"
        className={`fixed inset-0 z-[1300] bg-[rgba(38,32,26,0.32)] transition-opacity duration-220 ease-out motion-reduce:transition-none ${
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Help Center"
        tabIndex={-1}
        className={`fixed inset-y-0 right-0 z-[1301] flex w-[66.67%] max-w-[960px] flex-col bg-white font-lato shadow-[-18px_0_48px_rgba(51,41,30,0.14)] outline-none transition-transform duration-240 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none max-[900px]:w-full max-[900px]:max-w-none ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-none items-center gap-3.5 border-card-border border-b px-[22px] py-4 max-[900px]:px-4">
          <HelpSearchBox
            value={query}
            onChange={changeQuery}
            onClear={clearSearch}
            inputRef={searchInputRef}
          />
          {isCoraEnabled() && (
            <button
              type="button"
              onClick={selectChat}
              aria-current={isChatShown ? "true" : undefined}
              className={`flex shrink-0 cursor-pointer items-center self-stretch whitespace-nowrap rounded border border-smoke px-3 font-lato text-[13px] transition-colors hover:border-dark-dust hover:text-dark-dust focus-visible:outline focus-visible:outline-2 focus-visible:outline-dust-ink focus-visible:outline-offset-2 min-[901px]:hidden ${
                isChatShown ? "bg-light-dust text-rodeo-black" : "bg-white text-graphite"
              }`}
            >
              {CORA_UI.ASK_CORA}
            </button>
          )}
          {view.kind === "chat" && (
            <button
              type="button"
              onClick={backToHelp}
              className="flex shrink-0 cursor-pointer items-center self-stretch whitespace-nowrap rounded border border-smoke bg-white px-3 font-lato text-[13px] text-graphite transition-colors hover:border-dark-dust hover:text-dark-dust focus-visible:outline focus-visible:outline-2 focus-visible:outline-dust-ink focus-visible:outline-offset-2 min-[901px]:hidden"
            >
              Back to help
            </button>
          )}
          <button
            type="button"
            onClick={close}
            aria-label="Close Help Center"
            className="flex aspect-square shrink-0 cursor-pointer self-stretch items-center justify-center rounded border border-smoke bg-white text-graphite transition-colors hover:border-dark-dust hover:text-dark-dust focus-visible:outline focus-visible:outline-2 focus-visible:outline-dust-ink focus-visible:outline-offset-2"
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1">
          <HelpPanelRail
            view={view}
            screenLabel={screenContext.label}
            onSelectScreen={resetSearch}
            onSelectSection={(s) => selectSection(s, false)}
            onSelectChat={selectChat}
            onTalkToHuman={talkToHuman}
            onSelectWatch={selectWatch}
          />

          <div
            data-help-pane="true"
            className={`flex min-w-0 flex-1 flex-col ${
              view.kind === "chat"
                ? "overflow-hidden"
                : "overflow-y-auto px-10 pt-[30px] pb-12 [overscroll-behavior:contain] max-[900px]:px-5 max-[900px]:pt-6 max-[900px]:pb-7"
            }`}
          >
            {view.kind === "screen" && (
              <HelpScreenView
                contextKeys={helpScreenRequestKeys(screenContext.keys)}
                screenLabel={screenContext.label}
                focusOnShow={viewTakesFocus}
                onSelectArticle={(slug) => showView({ kind: "article", slug }, true)}
                onSelectSection={(s) => selectSection(s, true)}
              />
            )}
            {view.kind === "search" && (
              <HelpSearchResultsView
                query={debouncedQuery}
                focusOnShow={viewTakesFocus}
                onSelectArticle={(slug) => showView({ kind: "article", slug }, true)}
              />
            )}
            {view.kind === "section" && (
              <HelpSectionView
                section={view.section}
                focusOnShow={viewTakesFocus}
                onSelectArticle={(slug) =>
                  showView({ kind: "article", slug, fromSection: view.section }, true)
                }
              />
            )}
            {view.kind === "watch" && (
              <HelpWatchView
                focusOnShow={viewTakesFocus}
                onSelectArticle={(slug) =>
                  showView({ kind: "article", slug, fromWatch: true }, true)
                }
              />
            )}
            {view.kind === "chat" && (
              <CoraChatView
                conversation={conversation}
                onConversationChange={setConversation}
                screenKey={screenContext.keys[0] ?? null}
                focusOnShow={viewTakesFocus}
                onOpenArticle={(slug) => showView({ kind: "article", slug, fromChat: true }, true)}
                onTalkToHuman={requestHumanPrompt}
                focusNewConversationTick={focusNewConversationTick}
              />
            )}
            {view.kind === "article" && (
              <HelpArticleView
                slug={view.slug}
                backLabel={
                  view.fromChat
                    ? CORA_UI.BACK_TO_CORA
                    : view.fromWatch
                      ? HELP_WATCH.HEADING
                      : view.fromSection
                        ? HELP_TOPIC_LABELS[view.fromSection]
                        : hasSearchQuery
                          ? "Search results"
                          : (screenContext.label ?? HELP_RAIL_FALLBACK_LABEL)
                }
                focusOnShow={viewTakesFocus}
                onBack={() => {
                  if (view.fromChat) {
                    showView(CHAT_VIEW, true);
                  } else if (view.fromWatch) {
                    showView(WATCH_VIEW, true);
                  } else if (view.fromSection) {
                    showView({ kind: "section", section: view.fromSection }, true);
                  } else {
                    showView(hasSearchQuery ? SEARCH_VIEW : SCREEN_VIEW, true);
                  }
                }}
                onOpenArticle={(slug) =>
                  showView(
                    {
                      kind: "article",
                      slug,
                      fromSection: view.fromSection,
                      fromChat: view.fromChat,
                      fromWatch: view.fromWatch,
                    },
                    true,
                  )
                }
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default HelpPanel;
